import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import bell from '../../assets/images/bell.png';
import briefcase from '../../assets/images/briefcase.png';
import clipboard from '../../assets/images/clipboard-v2.png';
import confetti from '../../assets/images/confetti.png';
import flashlight from '../../assets/images/flashlight.png';
import magicWand from '../../assets/images/magic-wand.png';

export enum EmptyStateImageVariant {
  BELL = 'bell',
  BRIEFCASE = 'briefcase',
  CLIPBOARD = 'clipboard',
  CONFETTI = 'confetti',
  FLASHLIGHT = 'flashlight',
  MAGIC_WAND = 'magic-wand',
}

export enum EmptyStateVariant {
  BEIGE = 'beige',
  BLUE = 'blue',
  CUSTOM = 'custom',
  EMPTY = 'empty',
  ORANGE = 'orange',
}

export const emptyStateImages: Record<EmptyStateImageVariant, string> = {
  [EmptyStateImageVariant.CLIPBOARD]: clipboard,
  [EmptyStateImageVariant.FLASHLIGHT]: flashlight,
  [EmptyStateImageVariant.BELL]: bell,
  [EmptyStateImageVariant.CONFETTI]: confetti,
  [EmptyStateImageVariant.BRIEFCASE]: briefcase,
  [EmptyStateImageVariant.MAGIC_WAND]: magicWand,
};

type ColorScheme = {
  borderClassName: string;
  centralCircle: { bgGradient: { from: string; to: string }; border: string };
  // The title's gradient, where a design draws it at another angle than the shared one.
  titleGradient?: string;
};

const TITLE_GRADIENT = 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)';
export const emptyStateColorScheme: Record<EmptyStateVariant, ColorScheme> = {
  [EmptyStateVariant.BLUE]: {
    centralCircle: {
      border: 'rgba(220, 104, 3, 0.13)',
      bgGradient: { from: 'rgba(254, 200, 75, 0.13)', to: 'rgba(220, 104, 3, 0.13)' },
    },
    borderClassName: 'radial-gradient-border-orange',
  },
  [EmptyStateVariant.CUSTOM]: {
    centralCircle: {
      border: '',
      bgGradient: { from: '', to: '' },
    },
    borderClassName: '',
  },
  [EmptyStateVariant.EMPTY]: {
    centralCircle: {
      border: 'rgba(220, 104, 3, 0.13)',
      bgGradient: { from: 'rgba(254, 200, 75, 0.13)', to: 'rgba(220, 104, 3, 0.13)' },
    },
    borderClassName: '',
  },
  [EmptyStateVariant.BEIGE]: {
    centralCircle: {
      border: 'rgba(220, 104, 3, 0.13)',
      bgGradient: { from: 'rgba(171, 94, 94, 0.13)', to: 'rgba(113, 81, 81, 0.13)' },
    },
    borderClassName: 'radial-gradient-border-beige',
  },
  [EmptyStateVariant.ORANGE]: {
    centralCircle: {
      border: 'rgba(220, 104, 3, 0.13)',
      bgGradient: { from: 'rgba(254, 200, 75, 0.13)', to: 'rgba(220, 104, 3, 0.13)' },
    },
    borderClassName: 'radial-gradient-border-yellow',
  },
};

export type EmptyStateProps = {
  actions?: ReactNode;
  children?: ReactNode;
  colorScheme?: EmptyStateVariant;
  description?: ReactNode | string;
  imageVariant?: EmptyStateImageVariant;
  text: string;
} & OR<
  { colorScheme: EmptyStateVariant.CUSTOM; scheme: ColorScheme },
  { colorScheme: EmptyStateVariant }
>;
export const EmptyState: FC<EmptyStateProps> = ({
  actions,
  children,
  text,
  description,
  imageVariant = EmptyStateImageVariant.CLIPBOARD,
  colorScheme = EmptyStateVariant.ORANGE,
  scheme,
}) => {
  const {
    borderClassName,
    centralCircle,
    titleGradient = TITLE_GRADIENT,
  } = colorScheme === EmptyStateVariant.CUSTOM && scheme
    ? scheme
    : emptyStateColorScheme[colorScheme];

  return (
    <div
      className={twMerge(
        'centered relative z-1 m-auto my-20 flex h-fit w-full max-w-[393px] flex-col gap-6 rounded-2xl px-6 py-8',
        borderClassName,
      )}
    >
      <div
        className="rounded-full bg-white p-3"
        style={{
          boxShadow: `0px 0px 8px 0px ${centralCircle.border} inset`,
        }}
      >
        <div
          className="centered flex size-28 items-center gap-3 rounded-full"
          style={{
            background: `linear-gradient(180deg, ${centralCircle.bgGradient.from} 0%, ${centralCircle.bgGradient.to} 100%)`,
          }}
        >
          <div className="size-[56px] object-cover">
            <img alt="Empty list" src={emptyStateImages[imageVariant]} />
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-2">
        <span
          className="!bg-clip-text text-xl font-semibold text-transparent"
          style={{ background: titleGradient }}
        >
          {text}
        </span>
        {description ? (
          <span className="max-w-[323px] text-center text-xs font-[450] text-gray-500">
            {description}
          </span>
        ) : null}
      </div>

      {actions ? <div className="flex items-center justify-center gap-3">{actions}</div> : null}

      {children ? (
        <span className="line-clamp-2 max-w-[240px] text-center text-xs font-[450] text-gray-500">
          {children}
        </span>
      ) : null}
    </div>
  );
};
